import { announce } from '../announce.js';

const MAX_ZOOM = 4;
const PADDING = 24;
const OUTPUT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * <mellow-cropper>: crops an image to a circle, square or aspect ratio. The image can be
 * dragged (pointer, touch, arrow keys), zoomed (slider, wheel, pinch, +/-), rotated in
 * 90° steps and straightened. It always covers the crop area, so the result has no gaps.
 *
 * Used by <mellow:file-upload crop="…">: the chosen image is cropped before it reaches
 * the file input (and wire:model).
 */
export class MellowCropper extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.state = { zoom: 1, quarter: 0, angle: 0, x: 0, y: 0 };
        this.pointers = new Map();

        const stage = this.stage;
        stage.addEventListener('pointerdown', this.onPointerDown);
        stage.addEventListener('pointermove', this.onPointerMove);
        stage.addEventListener('pointerup', this.onPointerUp);
        stage.addEventListener('pointercancel', this.onPointerUp);
        stage.addEventListener('wheel', this.onWheel, { passive: false });
        stage.addEventListener('keydown', this.onKeydown);

        this.addEventListener('input', (event) => {
            // The zoom slider works in percent (100–400).
            if (event.target.matches('[data-cropper-zoom]')) this.setZoom(Number(event.target.value) / 100);
            if (event.target.matches('[data-cropper-angle]')) this.update({ angle: Number(event.target.value) });
        });

        this.addEventListener('click', (event) => {
            const rotate = event.target.closest('[data-cropper-rotate]');
            if (rotate) this.rotate(Number(rotate.dataset.cropperRotate));
            if (event.target.closest('[data-cropper-reset]')) this.reset();
        });

        new ResizeObserver(() => this.draw()).observe(stage);
    }

    get stage() {
        return this.querySelector('.mellow-cropper-stage');
    }

    get canvas() {
        return this.querySelector('.mellow-cropper-canvas');
    }

    get aspect() {
        return Number(this.dataset.aspect) || 1;
    }

    get circle() {
        return this.dataset.shape === 'circle';
    }

    get rotation() {
        return ((this.state.quarter * 90 + this.state.angle) * Math.PI) / 180;
    }

    async load(file) {
        // Reset first: adjustments made while the image decodes are kept.
        this.image = null;
        this.file = file;
        this.reset();

        const url = URL.createObjectURL(file);
        const image = new Image();
        image.src = url;

        try {
            await image.decode();
        } finally {
            URL.revokeObjectURL(url);
        }

        this.image = image;
        this.update();
    }

    reset() {
        this.state = { zoom: 1, quarter: 0, angle: 0, x: 0, y: 0 };
        this.update();
    }

    rotate(direction) {
        this.update({ quarter: (this.state.quarter + direction + 4) % 4 });
        announce(`${(this.state.quarter * 90 + this.state.angle + 360) % 360}°`);
    }

    setZoom(zoom) {
        const previous = this.state.zoom;
        const next = Math.min(MAX_ZOOM, Math.max(1, zoom));
        // Zoom around the crop center: scale the offset with the image.
        const factor = next / previous;
        this.update({ zoom: next, x: this.state.x * factor, y: this.state.y * factor });
    }

    update(changes = {}) {
        Object.assign(this.state, changes);
        this.clamp();
        this.syncControls();
        this.draw();
    }

    /** The crop area in stage pixels: as large as possible, centered, with the target aspect ratio. */
    area() {
        const { width, height } = this.stage.getBoundingClientRect();
        let w = Math.max(1, width - PADDING * 2);
        let h = w / this.aspect;
        if (h > height - PADDING * 2) {
            h = Math.max(1, height - PADDING * 2);
            w = h * this.aspect;
        }
        return { width, height, w, h };
    }

    /** Scale at which the rotated image just covers the crop area; zoom multiplies it. */
    scale(area = this.area()) {
        if (!this.image) return 1;
        const { naturalWidth: iw, naturalHeight: ih } = this.image;
        const [a, b] = this.extents(area);
        return Math.max((2 * a) / iw, (2 * b) / ih) * this.state.zoom;
    }

    /** Half extents of the crop area in the image's (rotated) frame. */
    extents({ w, h }) {
        const cos = Math.abs(Math.cos(this.rotation));
        const sin = Math.abs(Math.sin(this.rotation));
        return [(w / 2) * cos + (h / 2) * sin, (w / 2) * sin + (h / 2) * cos];
    }

    /** Keep the crop area inside the image: clamp the offset in the image's frame. */
    clamp() {
        if (!this.image) return;
        const area = this.area();
        const scale = this.scale(area);
        const [a, b] = this.extents(area);
        const t = this.rotation;
        const [cos, sin] = [Math.cos(t), Math.sin(t)];

        const limitX = Math.max(0, (this.image.naturalWidth * scale) / 2 - a);
        const limitY = Math.max(0, (this.image.naturalHeight * scale) / 2 - b);

        let lx = this.state.x * cos + this.state.y * sin;
        let ly = -this.state.x * sin + this.state.y * cos;
        lx = Math.min(limitX, Math.max(-limitX, lx));
        ly = Math.min(limitY, Math.max(-limitY, ly));

        this.state.x = lx * cos - ly * sin;
        this.state.y = lx * sin + ly * cos;
    }

    syncControls() {
        const zoom = this.querySelector('[data-cropper-zoom]');
        const percent = Math.round(this.state.zoom * 100);
        if (zoom && Number(zoom.value) !== percent) zoom.value = percent;
        const angle = this.querySelector('[data-cropper-angle]');
        if (angle && Number(angle.value) !== this.state.angle) angle.value = this.state.angle;
    }

    paint(context, { centerX, centerY, factor, scale }) {
        const { naturalWidth: iw, naturalHeight: ih } = this.image;
        context.save();
        context.translate(centerX + this.state.x * factor, centerY + this.state.y * factor);
        context.rotate(this.rotation);
        context.scale(scale * factor, scale * factor);
        context.drawImage(this.image, -iw / 2, -ih / 2);
        context.restore();
    }

    draw() {
        const canvas = this.canvas;
        if (!canvas || !this.image) return;

        const area = this.area();
        const ratio = window.devicePixelRatio || 1;
        canvas.width = Math.round(area.width * ratio);
        canvas.height = Math.round(area.height * ratio);

        const context = canvas.getContext('2d');
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.clearRect(0, 0, area.width, area.height);

        const centerX = area.width / 2;
        const centerY = area.height / 2;
        this.paint(context, { centerX, centerY, factor: 1, scale: this.scale(area) });

        // Dim everything outside the crop area and outline it.
        const shape = new Path2D();
        if (this.circle) shape.ellipse(centerX, centerY, area.w / 2, area.h / 2, 0, 0, Math.PI * 2);
        else shape.rect(centerX - area.w / 2, centerY - area.h / 2, area.w, area.h);

        const outside = new Path2D();
        outside.rect(0, 0, area.width, area.height);
        outside.addPath(shape);

        context.fillStyle = 'rgb(0 0 0 / 0.55)';
        context.fill(outside, 'evenodd');
        context.strokeStyle = 'rgb(255 255 255 / 0.9)';
        context.lineWidth = 1.5;
        context.stroke(shape);
    }

    /**
     * Render the crop at `size` px on its longer side (never upscaling beyond the source)
     * and return it as a File of the original type (PNG for other formats).
     */
    async export() {
        const area = this.area();
        const scale = this.scale(area);
        const size = Number(this.dataset.size) || 512;

        // Source pixels covered by the crop area – upscaling beyond that only adds blur.
        const sourceWidth = area.w / scale;
        const longest = Math.min(size, Math.max(sourceWidth, sourceWidth / this.aspect));
        const width = Math.max(1, Math.round(this.aspect >= 1 ? longest : longest * this.aspect));
        const height = Math.max(1, Math.round(width / this.aspect));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        context.imageSmoothingQuality = 'high';

        const type = OUTPUT_TYPES.includes(this.file?.type) ? this.file.type : 'image/png';
        if (type === 'image/jpeg') {
            context.fillStyle = '#fff';
            context.fillRect(0, 0, width, height);
        }

        this.paint(context, { centerX: width / 2, centerY: height / 2, factor: width / area.w, scale });

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, 0.92));
        const extension = type.split('/')[1].replace('jpeg', 'jpg');
        const name = (this.file?.name ?? 'image').replace(/\.[^.]+$/, '') + `.${extension}`;

        return new File([blob], name, { type, lastModified: Date.now() });
    }

    onPointerDown = (event) => {
        if (event.button !== 0 && event.pointerType === 'mouse') return;
        this.stage.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.stage.setAttribute('data-dragging', '');
    };

    onPointerMove = (event) => {
        const previous = this.pointers.get(event.pointerId);
        if (!previous) return;

        const current = { x: event.clientX, y: event.clientY };

        if (this.pointers.size === 1) {
            this.update({ x: this.state.x + current.x - previous.x, y: this.state.y + current.y - previous.y });
        } else if (this.pointers.size === 2) {
            // Pinch: zoom by the change of distance between both fingers.
            const other = [...this.pointers.entries()].find(([id]) => id !== event.pointerId)[1];
            const before = Math.hypot(previous.x - other.x, previous.y - other.y);
            const after = Math.hypot(current.x - other.x, current.y - other.y);
            if (before > 0) this.setZoom(this.state.zoom * (after / before));
        }

        this.pointers.set(event.pointerId, current);
    };

    onPointerUp = (event) => {
        this.pointers.delete(event.pointerId);
        if (!this.pointers.size) this.stage.removeAttribute('data-dragging');
    };

    onWheel = (event) => {
        event.preventDefault();
        this.setZoom(this.state.zoom * Math.exp(-event.deltaY * 0.002));
    };

    onKeydown = (event) => {
        const step = event.shiftKey ? 40 : 10;
        const moves = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };

        if (moves[event.key]) {
            // Arrow keys move the section over the image (like moving a viewfinder).
            const [dx, dy] = moves[event.key];
            this.update({ x: this.state.x + dx, y: this.state.y + dy });
        } else if (event.key === '+' || event.key === '=') {
            this.setZoom(this.state.zoom * 1.1);
        } else if (event.key === '-') {
            this.setZoom(this.state.zoom / 1.1);
        } else if (event.key.toLowerCase() === 'r') {
            this.rotate(event.shiftKey ? -1 : 1);
        } else {
            return;
        }
        event.preventDefault();
    };
}

/**
 * <mellow:file-upload crop="…">: intercept the chosen image, let the user crop it in the
 * dialog, then hand the cropped file to the input (and Livewire) instead.
 */
export function registerCropper() {
    const intercept = (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'file') return;

        const zone = input.closest('[data-mellow-crop]');
        if (!zone) return;

        // Our own events carrying the cropped file pass through.
        if (input._mellowCropped > 0) {
            input._mellowCropped--;
            return;
        }

        const file = input.files?.[0];
        if (!file || !file.type.startsWith('image/')) return;

        event.stopImmediatePropagation();
        if (event.type === 'change') open(zone, input, file);
    };

    document.addEventListener('input', intercept, true);
    document.addEventListener('change', intercept, true);

    const commit = (input, file) => {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
        input._mellowCropped = 2;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const open = async (zone, input, file) => {
        const modal = document.querySelector(`mellow-modal[name="${CSS.escape(zone.dataset.mellowCrop)}"]`);
        const cropper = modal?.querySelector('mellow-cropper');
        if (!cropper) return commit(input, file);

        cropper._input = input;
        cropper._applied = false;
        modal.show();

        try {
            await cropper.load(file);
        } catch {
            // Not decodable in this browser (e.g. HEIC): upload the original and let the server decide.
            cropper._applied = true;
            modal.close();
            commit(input, file);
        }
    };

    document.addEventListener('click', async (event) => {
        const apply = event.target.closest('[data-mellow-cropper-apply]');
        const modal = apply?.closest('mellow-modal');
        const cropper = modal?.querySelector('mellow-cropper');
        if (!cropper?._input || !cropper.image) return;

        apply.disabled = true;
        try {
            const file = await cropper.export();
            cropper._applied = true;
            modal.close();
            commit(cropper._input, file);
        } finally {
            apply.disabled = false;
        }
    });

    // Closing without applying discards the selection, so the same file can be chosen again.
    document.addEventListener('mellow:close', (event) => {
        const cropper = event.target.querySelector?.('mellow-cropper');
        if (!cropper?._input) return;
        if (!cropper._applied) cropper._input.value = '';
        cropper._input = null;
    });
}
